import axios from "axios";
import { defineStore } from "pinia";

export const useAppStore = defineStore("app", {
  state: () => ({
    user: null,
    busy: false,
    server: "http://localhost:8000/",
    // Menu yang disembunyikan untuk tenant ini (config.json "sembunyikan_menu": ["/audit_log", ...]),
    // dipakai saat API tenant belum punya endpoint menu itu - satu build untuk semua tenant.
    menuTersembunyi: [],
    setting: {},
    // Masa langganan dari GET /api/langganan: { berakhir: 'Y-m-d'|null, sisa_hari, habis (= terkunci),
    // masa_tenggang, terkunci_pada, sisa_hari_tenggang }. null = API
    // tenant belum punya endpoint itu (atau gagal dibaca): chip dan peringatan tidak tampil.
    langganan: null,
    // Per-tenant feature flags read from the API. tugas_opname: stok_opname_tugas/status aktif;
    // null = not read yet. The Tugas Opname menu and routes show only when it is true.
    // wa_nota (UAT #1688): wa_nota/status aktif - the WhatsApp Nota page (store number pairing).
    fitur: { tugas_opname: null, wa_nota: null, perangkat_cetak: null },
  }),
  getters: {
    token() {
      return this.user?.token || '';
    },
    baseApiUrl() {
      return this.server + "api/"
    },
    isLogin() {
      return this.user != null;
    },
    langgananHabis() {
      return !!this.langganan?.habis;
    },
    baseFileUrl() {
      return this.server + "api/file/"
    },
    baseFileFotoUrl() {
      return this.server + "api/file_foto/"
    },
    apiUrl() {
      return this.server + "api/admin/"
    },
  },
  actions: {
    // FITUR_TUGAS_OPNAME on this tenant? Any failure (an API without the endpoint, offline) counts
    // as off: that tenant cannot serve the feature anyway.
    loadFitur() {
      if (!this.user) {
        this.fitur = { tugas_opname: null, wa_nota: null, perangkat_cetak: null };
        return Promise.resolve(this.fitur);
      }
      const baca = (url, kunci) =>
        this.post(url, {})
          .then((d) => (this.fitur = { ...this.fitur, [kunci]: d?.aktif === true }))
          .catch(() => (this.fitur = { ...this.fitur, [kunci]: false }));
      return Promise.all([baca("stok_opname_tugas/status", "tugas_opname"), baca("wa_nota/status", "wa_nota"),
        baca("perangkat_cetak/cabang", "perangkat_cetak")])
        .then(() => this.fitur);
    },
    loadLangganan() {
      return axios.get(this.baseApiUrl + "langganan")
        .then((res) => {
          const d = res.data?.ok ? res.data.data : null;
          this.langganan = d && d.berakhir ? d : null;
          return this.langganan;
        })
        .catch(() => (this.langganan = null));
    },
    // Jawaban API terkunci (CekLangganan, HTTP 200 kode LANGGANAN_HABIS): tampilkan layar terkunci
    // tanpa menunggu /api/langganan, lalu baca tanggalnya untuk isi layar itu.
    tandaiLangganan(api) {
      if (api?.kode !== "LANGGANAN_HABIS") return;
      if (!this.langganan?.habis) {
        this.langganan = { ...(this.langganan || {}), habis: true };
        this.loadLangganan();
      }
    },
    loadSetting() {
      this.busy = true;
      this.getSettingAplikasi()
        .then((s) => {
          this.setting = s;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    // Satu keputusan untuk semua daftar menu (side nav dan dialog Menu Aplikasi): hak akses DAN tidak
    // disembunyikan untuk tenant ini lewat config.json "sembunyikan_menu".
    menuTampil(item) {
      const jalur = item.path && !item.path.startsWith("/") ? `/${item.path}` : item.path;
      if (item.key === "stok_opname_tugas" && this.fitur.tugas_opname !== true) return false;
      if (item.key === "wa_nota" && this.fitur.wa_nota !== true) return false;
      if (item.key === "perangkat_cetak" && this.fitur.perangkat_cetak !== true) return false;
      return this.boleh(`${item.key}:lihat`) && !this.menuTersembunyi.includes(jalur);
    },
    boleh(akses) {
      let u = this.user;
      if (u) {
        if (u.is_admin == 1 || u.is_backdoor == 1) {
          return true
        }
        let itm = u.konfigurasi?.find(x => x == akses)
        return !!itm
      } else {
        return false
      }
    },
    getDefaultImageUrl() {
      return this.getFileUrl("default/foto.jpeg")
    },
    getFileUrl(file_path) {
      if (file_path) {
        return this.baseFileUrl + file_path
      }
      return null
    },
    login(user_name, password, remember_me) {
      return new Promise((resolve, reject) => {
        this.busy = true
        let u
        try {
          this.post('login', { user_name, password, remember_me })
            .then(u => {
              u.konfigurasi = JSON.parse(u.konfigurasi);
              localStorage.setItem('user', JSON.stringify(u));
              this.user = u;
              this.loadFitur();
              resolve(u)
            })
            .catch(e => reject(e))
        } catch (error) {
          console.error(error)
          reject(error)
        } finally {
          this.busy = false
        }
        return u;
      })
    },
    logout() {
      this.busy = true;
      return new Promise((resolve) => {
        localStorage.removeItem('user');
        this.user = null;
        this.fitur = { tugas_opname: null };
        this.post('logout', {})
          .catch(e => console.error(e))
          .then(() => {
            this.busy = false;
            resolve();
          });
      })
    },
    getSettingAplikasi() {
      return this.post('setting/aplikasi', {})
    },
    loadServerUrl() {
      this.busy = true;
      return axios.get("/config.json")
        .then((response) => {
          let config = response.data;
          // make sure it ends with /
          let s = config.api_url;
          if (!s.endsWith("/")) {
            s += "/";
          }
          this.server = s;
          this.menuTersembunyi = (Array.isArray(config.sembunyikan_menu) ? config.sembunyikan_menu : [])
            .map((p) => (p.startsWith("/") ? p : `/${p}`));
        })
        .catch(e => console.error(e))
        .then(() => this.busy = false);

    },
    restoreLogin() {
      this.busy = true;
      try {
        let s = localStorage.getItem("user");
        if (s) {
          try {
            let u = JSON.parse(s);
            this.user = u;
            return u;
          } catch (error) {
            console.error(error);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.busy = false;
      }
      return null;
    },
    getFileFotoUrl(id) {
      if (!id) {
        return null
      }
      return this.baseFileFotoUrl + id
    },
    async regularPost(url, data) {
      let token = this.user?.token || '';
      try {
        let res = await axios.post(this.apiUrl + url, data, {
          headers: {
            token
          }
        });
        this.tandaiLangganan(res.data);
        return res
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async post(url, data) {
      let token = this.user?.token || '';
      try {
        let res = await axios.post(this.apiUrl + url, data, {
          headers: {
            token
          }
        });
        let api = res.data
        this.tandaiLangganan(api);
        // the flag was switched off under us: hide the menu too (the message itself is thrown below)
        if (api?.kode === "FITUR_NONAKTIF" && this.fitur.tugas_opname !== false) {
          this.fitur = { ...this.fitur, tugas_opname: false };
        }
        if (!api.ok) {
          let err;
          try {
            err = new Error(api.data.join('<br/>'));
          } catch (er) {
            err = new Error(api.msg)
          }
          // An API refusal shown to the user, not a crash: the crash reporter skips these.
          err.isApiError = true;
          throw err;
        }
        return api.data
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    postFile(url, data) {
      let token = this.user?.token || '';
      return new Promise((resolve, reject) => {
        axios
          .post(`${this.apiUrl}${url}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
              token,
            }
          })
          .then((res) => {
            let api = res.data;
            this.tandaiLangganan(api);
            if (!api.ok) {
              reject(api.msg);
            } else {
              resolve(api.data);
            }
          })
          .catch((e) => reject(e));
      });
    },
    async changePassword(email, recoveryCode) {
      try {
        // Send request
        console.log(email, recoveryCode);

        return {
          isOk: true
        };
      }
      catch {
        return {
          isOk: false,
          message: "Failed to change password"
        }
      }
    },

    async createAccount(nama, user_name, password) {
      try {
        // Send request
        await this.post('register', { nama, user_name, password });
        // this._user = { ...defaultUser, user_name: user_name };
        // ApiHelper.token = this._user.token;
        return {
          isOk: true
        };
      }
      catch (e) {
        console.error(e)
        return {
          isOk: false,
          message: e.message
        };
      }
    },
  }
});